import React from "react";
import { Link } from "react-router-dom";

const SmartFormBuilder = () => {
  const highlights = [
    {
      title: "Category-Based Templates",
      desc: "Choose from a library of professionally designed templates categorized by industry—Real Estate, E-commerce, Education, and more. Start with a foundation that works.",
      icon: "category",
    },
    {
      title: "Total Theme Control",
      desc: "Match your website’s DNA. Customize fonts, button styles, input shapes, and color palettes. Your forms will look like they were built in-house, not like a third-party plugin.",
      icon: "palette",
    },
    {
      title: "Flexible Deployment",
      desc: "Get your form live in seconds. Copy the clean HTML/CSS public code for deep integration, or use our lightweight Iframe for a zero-conflict setup on any platform.",
      icon: "code_blocks",
    },
  ];

  return (
    <section
      id="form-builder"
      className="py-16 sm:py-20 lg:py-24 bg-white overflow-hidden border-t border-[#f1f5f9]"
    >
      <div className="mx-auto w-[95%] md:w-[90%]">
        <div className="flex flex-col-reverse lg:flex-row gap-10 lg:gap-16 items-center">
          {/* VISUAL */}
          <div className="w-full lg:w-1/2 relative opacity-0 animate-fade-in-up pb-10 sm:pb-8">
            <div className="relative rounded-2xl bg-white p-1 shadow-2xl border border-[#E2E8F0] overflow-hidden">
              <div className="bg-[#f8fafc] px-3 sm:px-4 py-3 flex items-center justify-between border-b border-[#E2E8F0] gap-2">
                <div className="flex gap-1.5 shrink-0">
                  <div className="size-2.5 rounded-full bg-[#cbd5e1]"></div>
                  <div className="size-2.5 rounded-full bg-[#cbd5e1]"></div>
                  <div className="size-2.5 rounded-full bg-[#cbd5e1]"></div>
                </div>
                <div className="flex items-center gap-2 sm:gap-4">
                  <div className="flex items-center gap-1 text-[9px] sm:text-[10px] font-bold text-[#64748B] whitespace-nowrap">
                    <span className="size-2 rounded-full bg-green-500 animate-pulse"></span>
                    ACTIVE
                  </div>
                  <div className="hidden sm:block h-4 w-px bg-[#E2E8F0]"></div>
                  <button
                    type="button"
                    className="text-[9px] sm:text-[10px] font-bold text-[#E63946] hover:underline whitespace-nowrap"
                  >
                    PAUSE FORM
                  </button>
                </div>
              </div>

              <div className="flex h-[300px] sm:h-[400px] lg:h-[450px]">
                <div className="hidden sm:block w-1/3 border-r border-[#E2E8F0] bg-[#f8fafc] p-4 space-y-6">
                  <div className="space-y-2">
                    <p className="text-[10px] font-black text-[#94A3B8] uppercase">
                      Theme Styles
                    </p>
                    <div className="flex gap-2">
                      <div className="size-5 rounded bg-[#E63946]"></div>
                      <div className="size-5 rounded bg-[#0B1C2D]"></div>
                      <div className="size-5 rounded border border-[#E2E8F0] bg-white"></div>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <p className="text-[10px] font-black text-[#94A3B8] uppercase">
                      Input Radius
                    </p>
                    <div className="h-1.5 w-full bg-[#E2E8F0] rounded-full relative">
                      <div className="absolute right-4 top-1/2 -translate-y-1/2 size-3 bg-white border-2 border-[#E63946] rounded-full"></div>
                    </div>
                  </div>
                  <div className="pt-4 border-t border-[#E2E8F0]">
                    <button
                      type="button"
                      className="w-full py-2 rounded bg-[#0B1C2D] text-white text-[10px] font-bold flex items-center justify-center gap-2"
                    >
                      <span className="material-symbols-outlined text-xs">
                        terminal
                      </span>
                      GET CODE
                    </button>
                  </div>
                </div>

                <div className="flex-1 p-5 sm:p-8 bg-white flex flex-col justify-center">
                  <div className="max-w-xs mx-auto w-full space-y-4">
                    <div className="space-y-1">
                      <label className="text-[10px] font-bold text-[#0B1C2D]">
                        Full Name
                      </label>
                      <div className="h-9 w-full rounded-md border border-[#E2E8F0] bg-slate-50"></div>
                    </div>
                    <div className="space-y-1">
                      <label className="text-[10px] font-bold text-[#0B1C2D]">
                        Inquiry Category
                      </label>
                      <div className="h-9 w-full rounded-md border border-[#E2E8F0] bg-slate-50 flex items-center px-3 justify-between gap-2">
                        <span className="text-[10px] text-[#94A3B8] truncate">
                          Select Template Type...
                        </span>
                        <span className="material-symbols-outlined text-xs shrink-0">
                          expand_more
                        </span>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="w-full h-10 rounded-md bg-[#E63946] text-white text-xs font-bold shadow-lg shadow-[#E63946]/20"
                    >
                      Send Message
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="absolute bottom-0 right-2 sm:bottom-0 sm:right-4 bg-white border border-[#E2E8F0] px-3 sm:px-4 py-3 sm:py-4 rounded-2xl shadow-xl flex items-center gap-3 sm:gap-4 animate-float max-w-[calc(100%-1rem)]">
              <div className="size-9 sm:size-10 shrink-0 rounded-full bg-red-50 text-[#E63946] flex items-center justify-center">
                <span className="material-symbols-outlined">iframe</span>
              </div>
              <div className="min-w-0">
                <div className="text-[9px] sm:text-[10px] font-black text-[#64748B] uppercase tracking-tighter">
                  Embed Ready
                </div>
                <div className="text-xs sm:text-sm font-bold text-[#0B1C2D] truncate">
                  Iframe & Public Code
                </div>
              </div>
            </div>
          </div>

          {/* CONTENT */}
          <div className="w-full lg:w-1/2">
            <h2 className="text-sm sm:text-base font-bold tracking-[0.2em] text-[#E63946] uppercase mb-3 sm:mb-4">
              Smart Form Builder
            </h2>
            <h3 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#0B1C2D] lg:text-5xl leading-[1.15]">
              Custom Forms Built for{" "}
              <br className="hidden sm:block" />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#0B1C2D] to-[#E63946]">
                Every Website Theme.
              </span>
            </h3>

            <p className="mt-5 sm:mt-6 text-base sm:text-lg leading-relaxed text-[#64748B]">
              Stop compromising your design with generic plugins. ContactHQ
              gives you a powerful visual engine to build, monitor, and deploy
              forms that feel like a native part of your digital experience.
            </p>

            <div className="mt-8 sm:mt-12 space-y-8 sm:space-y-10">
              {highlights.map((item, index) => (
                <div
                  key={index}
                  className="group flex gap-4 sm:gap-6 opacity-0 animate-fade-in-up"
                  style={{ animationDelay: `${index * 150}ms` }}
                >
                  <div className="flex-none flex items-center justify-center size-10 sm:size-12 rounded-xl bg-white text-[#0B1C2D] group-hover:bg-[#0B1C2D] group-hover:text-white transition-all duration-300 shadow-sm border border-[#E2E8F0]">
                    <span className="material-symbols-outlined">
                      {item.icon}
                    </span>
                  </div>
                  <div className="min-w-0">
                    <h4 className="text-base sm:text-lg font-bold text-[#0B1C2D] mb-1.5 sm:mb-2">
                      {item.title}
                    </h4>
                    <p className="text-[#64748B] leading-relaxed text-sm lg:text-base">
                      {item.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-10 sm:mt-14 p-4 sm:p-6 rounded-2xl bg-slate-50 border border-[#E2E8F0] flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4 sm:gap-6">
              <div className="flex items-center gap-4">
                <div className="size-10 shrink-0 rounded-full bg-white border border-[#E2E8F0] flex items-center justify-center text-[#0B1C2D]">
                  <span className="material-symbols-outlined">analytics</span>
                </div>
                <div>
                  <p className="text-xs font-bold text-[#0B1C2D]">
                    Real-time Monitoring
                  </p>
                  <p className="text-[10px] text-[#64748B]">
                    Track status, views, and conversions
                  </p>
                </div>
              </div>
              <Link
                to="/register"
                className="px-6 py-2.5 rounded-lg border-2 border-[#0B1C2D] text-[#0B1C2D] text-xs font-bold text-center hover:bg-[#0B1C2D] hover:text-white transition-all whitespace-nowrap"
              >
                Explore Templates
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SmartFormBuilder;
